import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Avatar, Box, Divider, Group } from '@mantine/core'
import { useMantineColorScheme } from '@mantine/core'
import {
  IoInformationCircleOutline,
  IoLogOut,
  IoMoonOutline,
  IoSearchOutline,
  IoSettingsOutline,
  IoSunnyOutline
} from 'react-icons/io5'
import { AppBadge, AppText } from '../common/ui'
import type { User } from '../models/user'

const PANEL_WIDTH = 240
const PANEL_OFFSET = 8

const MENU_ITEM_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '10px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left' as const,
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 13,
  color: 'var(--gs-text)',
  textDecoration: 'none',
  boxSizing: 'border-box' as const,
  '&:hover': {
    background: 'rgba(232,93,4,0.08)',
    color: 'var(--gs-accent)'
  },
  '&:disabled': {
    opacity: 1,
    cursor: 'default',
    '&:hover': { background: 'none', color: 'var(--gs-text)' }
  }
}

type MenuItemProps = {
  children: React.ReactNode;
  icon?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  color?: 'red';
  rightSection?: React.ReactNode;
  disabled?: boolean;
}

function SettingsMenuItem ({ children, icon, to, onClick, color, rightSection, disabled }: MenuItemProps) {
  const Component = to ? Link : 'button'
  return (
    <Box
      component={Component}
      to={to}
      type={to ? undefined : 'button'}
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...MENU_ITEM_SX,
        ...(color === 'red' ? { color: 'var(--gs-red)', '&:hover': { color: 'var(--gs-red)' } } : {})
      }}
    >
      <Group spacing="xs" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {icon}
        <span>{children}</span>
      </Group>
      {rightSection}
    </Box>
  )
}

type Props = {
  user: User | null;
  openFlagCount: number;
  onSettingsClick?: () => void;
  onLogout: () => void;
}

/** Gear menu — manual fixed coords + portal (Mantine Menu/Popover popper stuck at 0,0). */
export default function TopNavSettingsMenu ({ user, openFlagCount, onSettingsClick, onLogout }: Props) {
  const [opened, setOpened] = useState(false)
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({ visibility: 'hidden' })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()

  const close = useCallback(() => setOpened(false), [])

  const updatePosition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const left = Math.min(
      Math.max(8, rect.right - PANEL_WIDTH),
      window.innerWidth - PANEL_WIDTH - 8
    )
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + PANEL_OFFSET,
      left,
      width: PANEL_WIDTH,
      zIndex: 400,
      visibility: 'visible'
    })
  }, [])

  useLayoutEffect(() => {
    if (!opened) return
    updatePosition()
  }, [opened, updatePosition])

  useEffect(() => {
    if (!opened) return
    const onReflow = () => updatePosition()
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [opened, updatePosition])

  useEffect(() => {
    if (!opened) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [opened, close])

  const panel = opened
    ? createPortal(
      <Box
        ref={panelRef}
        role="menu"
        aria-label="Account menu"
        style={panelStyle}
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border-bright)',
          borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          padding: 4,
          maxHeight: 'calc(100vh - 80px)',
          overflowY: 'auto'
        }}
      >
        {user && (
          <>
            <SettingsMenuItem disabled icon={<Avatar size="sm" src={user.image} alt={user.displayName} radius="xl" />}>
              <div>
                <AppText size="sm" weight={600}>
                  {user.displayName}
                </AppText>
                <AppText size="xs" color="dimmed">
                  @{user.username}
                </AppText>
              </div>
            </SettingsMenuItem>
            <Divider sx={{ borderColor: 'var(--gs-border)' }} />
          </>
        )}
        <SettingsMenuItem icon={<IoSearchOutline size={16} />} to="/search" onClick={close}>
          Search
        </SettingsMenuItem>
        <SettingsMenuItem icon={<IoInformationCircleOutline size={16} />} to="/about" onClick={close}>
          About us
        </SettingsMenuItem>
        {user && (
          <SettingsMenuItem to="/profile" onClick={close}>
            Profile
          </SettingsMenuItem>
        )}
        <SettingsMenuItem
          icon={colorScheme === 'dark' ? <IoSunnyOutline size={16} /> : <IoMoonOutline size={16} />}
          onClick={() => {
            toggleColorScheme()
            close()
          }}
        >
          {colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
        </SettingsMenuItem>
        {user && (user.isModerator || user.isAdmin) && (
          <SettingsMenuItem
            to="/moderation"
            onClick={close}
            rightSection={
              openFlagCount > 0
                ? (
                <AppBadge size="xs" color="red" variant="filled">
                  {openFlagCount > 99 ? '99+' : openFlagCount}
                </AppBadge>
                  )
                : null
            }
          >
            Moderation
          </SettingsMenuItem>
        )}
        {user?.isAdmin && (
          <>
            <Divider sx={{ borderColor: 'var(--gs-border)' }} />
            <SettingsMenuItem to="/admin/users" onClick={close}>
              Admin: Users
            </SettingsMenuItem>
            <SettingsMenuItem to="/admin/overseer" onClick={close}>
              Admin: Overseer
            </SettingsMenuItem>
            <SettingsMenuItem to="/admin/content" onClick={close}>
              Admin: Content
            </SettingsMenuItem>
          </>
        )}
        <SettingsMenuItem
          icon={<IoSettingsOutline size={16} />}
          onClick={() => {
            close()
            onSettingsClick?.()
          }}
        >
          Settings
        </SettingsMenuItem>
        {user
          ? (
          <>
            <Divider sx={{ borderColor: 'var(--gs-border)' }} />
            <SettingsMenuItem
              color="red"
              icon={<IoLogOut size={16} />}
              onClick={() => {
                close()
                onLogout()
              }}
            >
              Logout
            </SettingsMenuItem>
          </>
            )
          : (
          <>
            <Divider sx={{ borderColor: 'var(--gs-border)' }} />
            <SettingsMenuItem to="/login" onClick={close}>
              Login
            </SettingsMenuItem>
            <SettingsMenuItem to="/register" onClick={close}>
              Register
            </SettingsMenuItem>
          </>
            )}
      </Box>,
      document.body
    )
    : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Menu"
        aria-expanded={opened}
        aria-haspopup="menu"
        className="top-nav-settings-trigger"
        onClick={() => {
          if (opened) {
            close()
          } else {
            setPanelStyle({ visibility: 'hidden' })
            setOpened(true)
          }
        }}
      >
        <IoSettingsOutline size={26} />
      </button>
      {panel}
    </>
  )
}
